import { PageSize } from '@/constant';
import {
	GetCommonInquiryBoardListParams,
	ICommonInquiryBoard,
	TPayloadUpdateSuccess,
} from '@/services';
import { mergeItemUpdated } from '@/utils';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from '..';
import {
	ICommonInquiryBoardAnswerDetailStore,
	IPayloadDeleteAnswerCommonInquiryBoardAnswerRequest,
	IPayloadDeleteCommonInquiryBoardAnswerRequest,
	IPayloadGetCommonInquiryBoardAnswerDetailRequest,
	IPayloadGetCommonInquiryBoardAnswerDetailSuccess,
	IPayloadGetCommonInquiryBoardAnswerListRequest,
	IPayloadGetCommonInquiryBoardAnswerListSuccess,
	IPayloadSaveAnswerCommonInquiryBoardAnswerRequest,
} from './common-inquiry-board-answer.type';
const InitParams: GetCommonInquiryBoardListParams = {
	page: 1,
	pageSize: PageSize.S,
	searchTypes: [],
};

const initialState = {
	fetching: false,
	fetchingAnswerDetail: false,
	list: [] as ICommonInquiryBoardAnswerDetailStore[],
	total: 0,
	detail: {} as ICommonInquiryBoardAnswerDetailStore,
	answerDetail: {} as ICommonInquiryBoardAnswerDetailStore,
	params: InitParams,
};

const commonInquiryBoardAnswerSlice = createSlice({
	initialState,
	name: 'commonInquiryBoardAnswer',
	reducers: {
		/**---- GET COMMON INQUIRY BOARD ANSWER LIST START ---- */
		getCommonInquiryBoardAnswerListRequest: (
			state,
			action: PayloadAction<IPayloadGetCommonInquiryBoardAnswerListRequest>
		) => {
			state.fetching = true;
			state.params = action?.payload;
		},
		getCommonInquiryBoardAnswerListSuccess: (
			state,
			action: PayloadAction<IPayloadGetCommonInquiryBoardAnswerListSuccess>
		) => {
			const { total, content } = action.payload;
			state.fetching = false;
			state.list = content;
			state.total = total;
		},
		getCommonInquiryBoardAnswerListFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET COMMON INQUIRY BOARD ANSWER LIST END ---- */

		/**---- GET COMMON INQUIRY BOARD ANSWER DETAIL START ---- */
		getCommonInquiryBoardAnswerDetailRequest: (
			state,
			action: PayloadAction<IPayloadGetCommonInquiryBoardAnswerDetailRequest>
		) => {
			state.fetching = true;
		},
		getCommonInquiryBoardAnswerDetailSuccess: (
			state,
			action: PayloadAction<IPayloadGetCommonInquiryBoardAnswerDetailSuccess>
		) => {
			const { detail } = action.payload;
			state.fetching = false;
			state.detail = detail;
		},
		getCommonInquiryBoardAnswerDetailFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET COMMON INQUIRY BOARD ANSWER DETAIL END ---- */

		/**---- GET COMMON INQUIRY BOARD ANSWER DETAIL ANSWER START ---- */
		getCommonInquiryBoardAnswerDetailAnswerRequest: (
			state,
			action: PayloadAction<IPayloadGetCommonInquiryBoardAnswerDetailRequest>
		) => {
			state.fetchingAnswerDetail = true;
		},
		getCommonInquiryBoardAnswerDetailAnswerSuccess: (
			state,
			action: PayloadAction<IPayloadGetCommonInquiryBoardAnswerDetailSuccess>
		) => {
			const { detail } = action.payload;
			state.fetchingAnswerDetail = false;
			state.answerDetail = detail;
		},
		getCommonInquiryBoardAnswerDetailAnswerFailure: (state) => {
			state.fetchingAnswerDetail = false;
		},
		/**---- GET COMMON INQUIRY BOARD ANSWER DETAIL ANSWER END ---- */

		/**---- DELETE COMMON INQUIRY BOARD ANSWER START ---- */
		deleteCommonInquiryBoardAnswerRequest: (
			state,
			action: PayloadAction<IPayloadDeleteCommonInquiryBoardAnswerRequest>
		) => {
			state.fetching = true;
		},
		deleteCommonInquiryBoardAnswerSuccess: (state) => {
			state.fetching = false;
		},
		deleteCommonInquiryBoardAnswerFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE COMMON INQUIRY BOARD ANSWER END ---- */

		/**---- SAVE ANSWER COMMON INQUIRY BOARD ANSWER START ---- */
		saveAnswerCommonInquiryBoardAnswerRequest: (
			state,
			action: PayloadAction<IPayloadSaveAnswerCommonInquiryBoardAnswerRequest>
		) => {
			state.fetching = true;
		},
		saveAnswerCommonInquiryBoardAnswerSuccess: (state) => {
			state.fetching = false;
		},
		saveAnswerCommonInquiryBoardAnswerFailure: (state) => {
			state.fetching = false;
		},
		/**---- SAVE ANSWER COMMON INQUIRY BOARD ANSWER END ---- */

		/**---- DELETE ANSWER COMMON INQUIRY BOARD ANSWER START ---- */
		deleteAnswerCommonInquiryBoardAnswerRequest: (
			state,
			action: PayloadAction<IPayloadDeleteAnswerCommonInquiryBoardAnswerRequest>
		) => {
			state.fetching = true;
		},
		deleteAnswerCommonInquiryBoardAnswerSuccess: (state) => {
			state.fetching = false;
		},
		deleteAnswerCommonInquiryBoardAnswerFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE ANSWER COMMON INQUIRY BOARD ANSWER END ---- */

		/**---- UPDATE COMMON INQUIRY BOARD ANSWER START ---- */
		updateCommonInquiryBoardAnswerDetailSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<ICommonInquiryBoard>>
		) => {
			const { data } = action.payload;
			const answerDetail = { ...state.answerDetail };
			state.answerDetail = mergeItemUpdated<
				ICommonInquiryBoardAnswerDetailStore,
				ICommonInquiryBoard
			>(answerDetail, data);
		},
		/**---- UPDATE COMMON INQUIRY BOARD ANSWER END ---- */
	},
});

export const CommonInquiryBoardAnswerSelectors = {
	selectFetching: (state: RootState) => state.commonInquiryBoardAnswer.fetching,
	selectFetchingAnswerDetail: (state: RootState) =>
		state.commonInquiryBoardAnswer.fetchingAnswerDetail,
	selectTotal: (state: RootState) => state.commonInquiryBoardAnswer.total,
	selectList: (state: RootState) => state.commonInquiryBoardAnswer.list,
	selectDetail: (state: RootState) => state.commonInquiryBoardAnswer.detail,
	selectAnswerDetail: (state: RootState) =>
		state.commonInquiryBoardAnswer.answerDetail,
	selectParams: (state: RootState) => state.commonInquiryBoardAnswer.params,
};

export const CommonInquiryBoardAnswerActions =
	commonInquiryBoardAnswerSlice.actions;
export const CommonInquiryBoardAnswerReducer =
	commonInquiryBoardAnswerSlice.reducer;
